import { useLayoutEffect, useState, type RefObject } from 'react';

type NameLineCount = 1 | 2;
const NAME_SELECTOR = '.comparison-card__name, .comparison-metric-card__name';

/** Measure natural wrapping independently of the currently reserved/clamped slot. */
const measureNameLines = (container: HTMLElement): NameLineCount | null => {
  const names = Array.from(container.querySelectorAll<HTMLElement>(NAME_SELECTOR));
  if (!names.length) return 1;

  // Read all source geometry before inserting probes; then batch layout reads.
  const probes = names.flatMap((name) => {
    const width = name.getBoundingClientRect().width;
    const lineHeight = Number.parseFloat(window.getComputedStyle(name).lineHeight);
    if (width <= 0 || !Number.isFinite(lineHeight) || lineHeight <= 0) return [];
    const probe = name.cloneNode(true) as HTMLElement;
    probe.removeAttribute('id');
    probe.setAttribute('aria-hidden', 'true');
    probe.setAttribute('data-company-name-probe', '');
    Object.assign(probe.style, {
      position: 'fixed', left: '0', top: '0', width: `${width}px`,
      minWidth: '0', maxWidth: 'none', height: 'auto', minHeight: '0', maxHeight: 'none',
      display: 'block', visibility: 'hidden', pointerEvents: 'none',
      whiteSpace: 'normal', overflow: 'visible', flex: 'none',
    });
    probe.style.setProperty('-webkit-line-clamp', 'unset');
    return [{ name, probe, lineHeight }];
  });
  // Hidden/unmeasurable containers retain the previous result until resized.
  if (!probes.length) return null;
  try {
    probes.forEach(({ name, probe }) => name.parentElement?.appendChild(probe));
    return probes.some(({ probe, lineHeight }) => (
      probe.getBoundingClientRect().height > lineHeight + 0.5
    )) ? 2 : 1;
  } finally {
    probes.forEach(({ probe }) => probe.remove());
  }
};

/** One shared line count for the currently rendered peer page, not each card. */
export const useCompanyNameLineCount = (
  containerRef: RefObject<HTMLDivElement>,
  cards: readonly unknown[],
  isLoading: boolean,
): NameLineCount => {
  const [lineCount, setLineCount] = useState<NameLineCount>(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    // Keep the last measured slot for skeletons while the next page loads.
    if (!container || isLoading) return undefined;
    let disposed = false;
    let frameId: number | null = null;
    const requestFrame = window.requestAnimationFrame ?? (
      (callback: FrameRequestCallback) => window.setTimeout(() => callback(0), 0)
    );
    const cancelFrame = window.cancelAnimationFrame ?? window.clearTimeout;
    const measure = () => {
      const next = measureNameLines(container);
      if (next !== null) setLineCount(current => current === next ? current : next);
    };
    const schedule = () => {
      if (disposed || frameId !== null) return;
      frameId = requestFrame(() => { frameId = null; if (!disposed) measure(); });
    };
    measure();

    // Observe name widths too: a column or ticker may change the available
    // name width without changing the full list width. Ignore height-only
    // notifications caused by switching between one and two reserved lines.
    const widths = new WeakMap<Element, number>();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
      let changed = false;
      entries.forEach(entry => {
        if (widths.get(entry.target) !== entry.contentRect.width) {
          widths.set(entry.target, entry.contentRect.width);
          changed = true;
        }
      });
      if (changed) schedule();
    });
    observer?.observe(container);
    container.querySelectorAll(NAME_SELECTOR).forEach(name => observer?.observe(name));
    window.addEventListener('resize', schedule);
    const fonts = document.fonts;
    fonts?.addEventListener?.('loadingdone', schedule);
    if (fonts?.ready) void fonts.ready.then(schedule);

    return () => {
      disposed = true;
      if (frameId !== null) cancelFrame(frameId);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      fonts?.removeEventListener?.('loadingdone', schedule);
    };
  }, [containerRef, cards, isLoading]);

  return lineCount;
};
